import React, { useState } from "react";
import axios from "axios";
import StepShell from "./StepShell";
import LoadDetails from "./LoadDetails";
import { dollars } from "./loadFormat";
import { errorCode, errorMessage } from "./apiError";
import { counterCents, offerState } from "./offerState";

// Exact, cents included: the server compares to the cent.
const toDollars = (cents) => (cents != null ? cents / 100 : null);

// Digits and up to two decimal places.
function cleanAmount(raw) {
  const [whole, ...rest] = raw.replace(/[^\d.]/g, "").split(".");
  const cents = rest.join("").slice(0, 2);
  return whole.slice(0, 6) + (rest.length ? `.${cents}` : "");
}

/*
 * Step 3. One dollar box, prefilled with the best price we know, and one button.
 * The broker's answer lands here too, after the reservation: a yes to
 * confirm, or a counter to book or answer with a new offer.
 */
export default function PriceStep({
  booking,
  tokenUrl,
  onDecline,
  onSubmitted,
  onNeedStep,
  onRefresh,
}) {
  const posted = booking.bid_required ? null : booking.posted_rate_cents;
  const state = offerState(booking);
  const counter = toDollars(counterCents(booking));
  const locked = state === "initial" && booking.price_editable === false;
  const offersOnly = isOffersOnly(booking);
  // The best price we know (broker counter, the carrier's own, then posted),
  // chosen by the server; never on a page that only books at the posted rate.
  const prefill = locked ? null : toDollars(booking.prefill_cents);
  const initial = state === "counter" ? counter : prefill ?? toDollars(posted);
  const [amount, setAmount] = useState(initial != null ? String(initial) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const value = Number(amount) || 0;
  const acceptedDollars = toDollars(booking.chr_accepted_price_cents);
  // The counter sent back unchanged books the load.
  const takesCounter =
    state === "counter_final" ||
    (state === "counter" && Math.round(value * 100) === counterCents(booking));
  const asked = locked
    ? toDollars(posted)
    : state === "counter_final"
      ? counter
      : value;
  const fee = feeFor(booking, asked);

  async function send() {
    setError("");
    setBusy(true);
    try {
      if (state === "accepted") {
        await axios.post(`${tokenUrl}/chr-accept-response`);
        onSubmitted({ kind: "chr_confirmed", amount: acceptedDollars });
        return;
      }
      if (state === "counter" || state === "counter_final") {
        const sent = takesCounter ? counter : value;
        const { data } = await axios.post(`${tokenUrl}/chr-counter-response`, {
          amount: sent,
        });
        onSubmitted({
          kind: data?.chr_book_confirmed ? "chr_confirmed" : "resubmitted",
          amount: sent,
        });
        return;
      }
      const sent = locked ? toDollars(posted) : value;
      const { data } = await axios.post(`${tokenUrl}/submit-price`, {
        amount: sent,
        ...(posted ? { posted_rate_cents_seen: posted } : {}),
      });
      // A repeat send reports what the first one registered.
      const registered = data?.already_reserved
        ? toDollars(data.negotiation_amount_cents ?? data.posted_rate_cents)
        : sent;
      onSubmitted({
        kind: "sent",
        amount: registered ?? sent,
        creditApplied: data?.credit_applied,
      });
    } catch (err) {
      const code = errorCode(err);
      if (code === "AGREEMENT_REQUIRED") return onNeedStep(1);
      if (code === "PAYMENT_METHOD_REQUIRED") return onNeedStep(2);
      if (code === "PRICE_CHANGED") {
        const fresh = await onRefresh();
        const now = fresh?.bid_required ? null : fresh?.posted_rate_cents;
        if (now != null) setAmount(String(toDollars(now)));
      }
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const bookAction = (price) => ({
    label: `Book the load · ${dollars(price ?? 0)}`,
    onClick: send,
    busy,
    busyLabel: "Booking…",
  });

  if (state === "accepted") {
    return (
      <StepShell
        question={`The broker said yes to ${dollars(acceptedDollars ?? 0)}`}
        onDecline={onDecline}
        action={bookAction(acceptedDollars)}
      >
        <p className="m-0 font-[Roboto] text-[16px] leading-snug text-slate-700">
          Book it now to lock it in. The broker doesn't hold the load while we
          wait, so tap within about 10 minutes.
        </p>
        <FeeBlock
          fee={feeFor(booking, acceptedDollars)}
          asked={acceptedDollars}
        />
        <LoadDetails load={booking.load_snapshot} postedRateCents={posted} />
        {error && <Error text={error} />}
      </StepShell>
    );
  }

  if (state === "counter_final") {
    return (
      <StepShell
        question={`The broker's final price is ${dollars(counter)}`}
        subtitle={`They won't take another offer. Do you want this load at ${dollars(counter)}?`}
        onDecline={onDecline}
        action={bookAction(counter)}
      >
        <FixedPrice amount={counter} caption="Final price from the broker" />
        <FeeBlock fee={fee} asked={asked} />
        <LoadDetails load={booking.load_snapshot} postedRateCents={posted} />
        {error && <Error text={error} />}
      </StepShell>
    );
  }

  if (state === "counter") {
    const offered = toDollars(booking.negotiation_amount_cents);
    return (
      <StepShell
        question={`The broker countered at ${dollars(counter)}`}
        subtitle={`${offered ? `You offered ${dollars(offered)}. ` : ""}Book it at ${dollars(counter)}, or send a new offer.`}
        onDecline={onDecline}
        action={
          takesCounter
            ? bookAction(counter)
            : {
                label: value
                  ? `Send my offer · ${dollars(value)}`
                  : "Send my offer",
                onClick: send,
                disabled: !value,
                busy,
                busyLabel: "Sending…",
              }
        }
      >
        <PriceInput
          value={amount}
          onChange={setAmount}
          label="Your offer in dollars"
        />
        <FeeBlock fee={fee} asked={asked} awaitsBroker={!takesCounter} />
        <LoadDetails load={booking.load_snapshot} postedRateCents={posted} />
        {error && <Error text={error} />}
      </StepShell>
    );
  }

  return (
    <StepShell
      question="How much do you want for this load?"
      onDecline={onDecline}
      note={
        offersOnly
          ? "This broker takes offers only. They have to accept your offer."
          : null
      }
      action={
        locked
          ? {
              label: `Take it for ${dollars(toDollars(posted))}`,
              onClick: send,
              busy,
              busyLabel: "Sending…",
            }
          : {
              label: value
                ? `Send my offer · ${dollars(value)}`
                : "Send my offer",
              onClick: send,
              disabled: !value,
              busy,
              busyLabel: "Sending…",
            }
      }
    >
      {locked ? (
        <FixedPrice
          amount={toDollars(posted)}
          caption="This load is only available at the posted rate."
        />
      ) : (
        <PriceInput
          value={amount}
          onChange={setAmount}
          label="Your offer in dollars"
        />
      )}
      <FeeBlock fee={fee} asked={asked} awaitsBroker />

      <LoadDetails load={booking.load_snapshot} postedRateCents={posted} />
      {error && <Error text={error} />}
    </StepShell>
  );
}

// Fee in dollars for the offer being sent: 0 when free, null when unknown.
function feeFor(booking, dollarsAsked) {
  if (booking.free_booking_available) return 0;
  const tiers = booking.fee_tiers;
  if (!tiers || !dollarsAsked) return null;
  const cents = Math.round(dollarsAsked * 100);
  const tier = tiers.find((t) => cents < t.below_cents);
  const fee = tier ? tier.fee_cents : booking.fee_top_cents;
  return fee == null ? null : fee / 100;
}

// The broker takes this carrier's offer but not a booking at the posted rate.
export function isOffersOnly(booking) {
  return (
    booking.allow_reserve === false &&
    Boolean(booking.allow_negotiation) &&
    !booking.bid_required
  );
}

function FixedPrice({ amount, caption }) {
  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white px-5 py-4">
      <p className="m-0 font-[Barlow_Condensed] text-[52px] font-bold leading-none text-slate-900 tabular-nums">
        {dollars(amount)}
      </p>
      <p className="m-0 mt-1 font-[Roboto] text-[14px] text-slate-500">
        {caption}
      </p>
    </div>
  );
}

function PriceInput({ value, onChange, label }) {
  return (
    <div className="relative">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-[18px] top-1/2 -translate-y-1/2 font-[Barlow_Condensed] text-[40px] font-semibold text-slate-400"
      >
        $
      </span>
      <input
        id="book-it-price"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(cleanAmount(e.target.value))}
        className="w-full rounded-2xl border-2 border-slate-300 bg-white py-4 pl-[50px] pr-4 font-[Barlow_Condensed] text-[52px] font-bold leading-none text-slate-900 tabular-nums focus:border-indigo-600 focus:outline-none focus:ring-4 focus:ring-indigo-100"
      />
    </div>
  );
}

function FeeBlock({ fee, asked, awaitsBroker = false }) {
  const keep =
    fee != null && asked ? `You keep the full ${dollars(asked)}. ` : "";
  if (fee == null && !awaitsBroker) return null;
  return (
    <div className="flex flex-col gap-0.5 font-[Roboto] text-[15px]">
      {fee === 0 && (
        <p className="m-0 font-semibold text-slate-900">No fee on this load</p>
      )}
      {fee > 0 && (
        <p className="m-0 text-balance font-semibold text-slate-900">
          Cinesis fee: {dollars(fee)}, only if you get this load
        </p>
      )}
      {(keep || awaitsBroker) && (
        <p className="m-0 text-slate-500">
          {keep}
          {awaitsBroker && "We'll text you the broker's answer."}
        </p>
      )}
    </div>
  );
}

function Error({ text }) {
  return <p className="m-0 font-[Roboto] text-[15px] text-red-700">{text}</p>;
}
